import React, { useState } from 'react';
import { Box, Button, Typography, IconButton, InputBase, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import LockIcon from '@material-ui/icons/Lock';
import PersonIcon from '@material-ui/icons/Person';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { usePlannerTheme } from '../../theme/plannerTheme';

// Identidade ecooGPS: gradiente de marca (cyan → teal → verde), igual ao
// ui-kit e à página de download da app. --on-brand (#03121c) para texto sobre
// o gradiente. Tons claros e escuros a seguir os tokens --surf-*.
const BRAND_GRAD = 'linear-gradient(100deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)';
const BRAND_GRAD_DARK = 'linear-gradient(100deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)';
const ON_BRAND = '#03121c';

const useStyles = makeStyles(() => ({
  root: {
    width: '100vw',
    height: '100vh',
    background: ({ dark }) => dark
      ? 'radial-gradient(1000px 440px at 50% -12%, rgba(47,213,150,0.12), transparent 60%), linear-gradient(180deg, #0b1a22 0%, #0d1e27 55%, #0f2430 100%)'
      : 'radial-gradient(1000px 440px at 50% -12%, rgba(47,213,150,0.16), transparent 60%), linear-gradient(180deg, #eaf7fb 0%, #f4fbfd 45%, #ffffff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  card: {
    width: 380,
    background: ({ dark }) => dark ? 'rgba(18,33,44,0.92)' : 'rgba(255,255,255,0.88)',
    backdropFilter: 'blur(24px)',
    border: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.16)' : '1px solid rgba(24,189,238,0.14)',
    borderRadius: 20,
    padding: '40px 36px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
    position: 'relative',
    zIndex: 1,
    boxShadow: ({ dark }) => dark
      ? '0 24px 60px rgba(0,0,0,0.5)'
      : '0 16px 48px rgba(24,189,238,0.14), 0 4px 16px rgba(15,39,51,0.06)',
  },
  logoWrap: {
    width: 64,
    height: 64,
    background: ({ dark }) => dark ? BRAND_GRAD_DARK : BRAND_GRAD,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(24,189,238,0.4)',
    marginBottom: 4,
  },
  title: { fontWeight: 800, fontSize: '1.5rem', color: ({ dark }) => dark ? '#eaf2f6' : '#0f2733', textAlign: 'center', lineHeight: 1.2 },
  subtitle: { fontSize: '0.78rem', color: ({ dark }) => dark ? '#9fb3c0' : '#5b7384', textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: -16 },
  form: { width: '100%', display: 'flex', flexDirection: 'column', gap: 14 },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: 5, width: '100%' },
  fieldLabel: { fontSize: '0.72rem', fontWeight: 600, color: ({ dark }) => dark ? '#9fb3c0' : '#46606f', letterSpacing: '0.04em', textTransform: 'uppercase' },
  fieldBox: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: ({ dark }) => dark ? '#0f2430' : '#ffffff',
    border: ({ dark }) => dark ? '1.5px solid rgba(110,193,228,0.18)' : '1.5px solid #e5eef3',
    borderRadius: 10, padding: '0 12px', height: 44,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:focus-within': { borderColor: '#18BDEE', boxShadow: '0 0 0 3px rgba(24,189,238,0.15)' },
    '&:hover': { borderColor: ({ dark }) => dark ? 'rgba(110,193,228,0.4)' : '#9cc7d8' },
  },
  fieldIcon: { color: ({ dark }) => dark ? '#6f93a6' : '#94a3b8', fontSize: 17, flexShrink: 0 },
  fieldInput: {
    flex: 1, fontSize: '0.88rem',
    color: ({ dark }) => dark ? '#eaf2f6' : '#0f2733',
    '& input': { padding: 0 },
    '& input::placeholder': { color: ({ dark }) => dark ? '#6f93a6' : '#94a3b8', opacity: 1 },
  },
  loginBtn: {
    background: ({ dark }) => dark ? BRAND_GRAD_DARK : BRAND_GRAD, color: ON_BRAND,
    fontWeight: 800, fontSize: '0.88rem', padding: '11px 0', borderRadius: 10,
    boxShadow: '0 4px 16px rgba(24,189,238,0.35)', marginTop: 4,
    '&:hover': { filter: 'brightness(1.05)', boxShadow: '0 6px 20px rgba(24,189,238,0.5)' },
    transition: 'all 0.2s ease',
  },
  error: {
    background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 8, padding: '8px 12px', color: ({ dark }) => dark ? '#fda4a4' : '#dc2626', fontSize: '0.75rem', textAlign: 'center', width: '100%',
  },
  footer: { fontSize: '0.62rem', color: ({ dark }) => dark ? '#5b7384' : '#94a3b8', textAlign: 'center', letterSpacing: '0.04em' },
  backBtn: {
    position: 'absolute',
    top: 16,
    left: 16,
    color: ({ dark }) => dark ? '#9fb3c0' : '#5b7384',
    '&:hover': { color: '#18BDEE', background: 'rgba(24,189,238,0.1)' },
  },
}));

function LoginScreen({ onLogin, title, subtitle, onBack, onRegister, fieldLabel, fieldPlaceholder }) {
  const dark = usePlannerTheme() === 'dark';
  const classes = useStyles({ dark });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await onLogin(email, password);
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.card} style={{ animation: shaking ? 'shake 0.4s ease' : 'none' }}>
        {onBack && (
          <IconButton className={classes.backBtn} size="small" onClick={onBack}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        )}
        <Box className={classes.logoWrap}>
          <DirectionsCarIcon style={{ color: ON_BRAND, fontSize: 32 }} />
        </Box>
        <Typography className={classes.title}>{title || 'EcooGPS Planner'}</Typography>
        <Typography className={classes.subtitle}>{subtitle || 'Planeamento de rotas'}</Typography>
        <form className={classes.form} onSubmit={handleSubmit}>
          <Box className={classes.fieldWrap}>
            <Typography className={classes.fieldLabel}>{fieldLabel || 'Email'}</Typography>
            <Box className={classes.fieldBox}>
              <PersonIcon className={classes.fieldIcon} />
              <InputBase className={classes.fieldInput} placeholder={fieldPlaceholder || 'Introduza o email'} value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }} autoComplete="username" autoFocus />
            </Box>
          </Box>
          <Box className={classes.fieldWrap}>
            <Typography className={classes.fieldLabel}>Palavra-passe</Typography>
            <Box className={classes.fieldBox}>
              <LockIcon className={classes.fieldIcon} />
              <InputBase className={classes.fieldInput} placeholder="Introduza a palavra-passe"
                type={showPass ? 'text' : 'password'} value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }} autoComplete="current-password" />
              <IconButton size="small" onClick={() => setShowPass(v => !v)} style={{ color: dark ? '#6f93a6' : '#94a3b8', padding: 4 }}>
                {showPass ? <VisibilityOffIcon style={{ fontSize: 16 }} /> : <VisibilityIcon style={{ fontSize: 16 }} />}
              </IconButton>
            </Box>
          </Box>
          {error && <Box className={classes.error}>{error}</Box>}
          <Button type="submit" fullWidth variant="contained" className={classes.loginBtn} disableElevation disabled={loading}>
            {loading ? <CircularProgress size={20} style={{ color: ON_BRAND }} /> : 'Entrar'}
          </Button>
        </form>
        {onRegister && (
          <Typography
            style={{ fontSize: '0.78rem', color: dark ? '#9fb3c0' : '#5b7384', textAlign: 'center', cursor: 'pointer' }}
            onClick={onRegister}
            onMouseEnter={e => { e.target.style.color = '#18BDEE'; }}
            onMouseLeave={e => { e.target.style.color = dark ? '#9fb3c0' : '#5b7384'; }}
          >
            Não tem conta? <span style={{ fontWeight: 600 }}>Criar conta grátis</span>
          </Typography>
        )}
        <Typography className={classes.footer}>EcooGPS {new Date().getFullYear()} - Planeamento de rotas</Typography>
      </Box>
    </Box>
  );
}

export default LoginScreen;
